"use client"

import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { BILLING_PERMISSIONS, PARTNER_PROGRAM_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import {
  getMyClientBonusAction,
  getPartnerOverviewAction,
  getPartnerProgramAccessAction,
  listPartnerLedgerAction,
  listPartnerPayoutsAction,
  type PartnerLedgerPage,
  type PartnerOverview,
  type PartnerPayoutView,
} from "@/server-actions/partners"
import {
  getAdminPartnerDetailAction,
  getAdminProgramSettingsAction,
  listAdminPartnersAction,
  listAdminPayoutRequestsAction,
  type AdminPartnerDetail,
  type AdminPartnerView,
  type AdminPayoutRequestView,
  type AdminProgramSettings,
} from "@/server-actions/admin-partners"
import type { PartnerPayoutStatus } from "@/lib/partners/types"
import type { ClientBonusTerms } from "@/lib/partners/client-bonus-terms"

export const partnerProgramAccessQueryPrefix = ["partner-program-access"] as const

export const partnerProgramAccessQueryKey = (userId: string, tenantId: string) =>
  [...partnerProgramAccessQueryPrefix, userId, tenantId] as const

/** Runs only for users who could open the cabinet; `data` is undefined until known. */
export function usePartnerProgramAccessQuery() {
  const { user, isAuthenticated } = useAuth()
  const { can } = usePermissions()

  return useQuery<boolean>({
    queryKey: partnerProgramAccessQueryKey(user?.user_id ?? "unauthenticated", getQueryTenantId(user)),
    enabled: isAuthenticated && Boolean(user?.user_id) && can(PARTNER_PROGRAM_PERMISSIONS.MANAGE),
    queryFn: async () => {
      const result = await getPartnerProgramAccessAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result.enabled
    },
  })
}

export const clientBonusQueryPrefix = ["client-bonus"] as const

/** The tenant's bonus as a referred client; null while none applies. */
export function useClientBonusQuery() {
  const { user, isAuthenticated } = useAuth()
  const { can } = usePermissions()

  return useQuery<ClientBonusTerms | null>({
    queryKey: [...clientBonusQueryPrefix, user?.user_id ?? "unauthenticated", getQueryTenantId(user)],
    enabled: isAuthenticated && Boolean(user?.user_id) && can(BILLING_PERMISSIONS.VIEW),
    queryFn: async () => {
      const result = await getMyClientBonusAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result.bonus
    },
  })
}

export const partnerOverviewQueryPrefix = ["partner-overview"] as const

// Scoped by identity: the QueryClient outlives logout, so an unscoped key would
// hand the next user in the same tab the previous partner's balance and codes.
export const partnerOverviewQueryKey = (userId: string, tenantId: string) =>
  [...partnerOverviewQueryPrefix, userId, tenantId] as const

export function usePartnerOverviewQuery() {
  const { user, isAuthenticated } = useAuth()

  return useQuery<PartnerOverview>({
    queryKey: partnerOverviewQueryKey(user?.user_id ?? "unauthenticated", getQueryTenantId(user)),
    enabled: isAuthenticated && Boolean(user?.user_id),
    queryFn: async () => {
      const result = await getPartnerOverviewAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const partnerLedgerQueryPrefix = ["partner-ledger"] as const

export const partnerLedgerQueryKey = (userId: string, tenantId: string, limit: number) =>
  [...partnerLedgerQueryPrefix, userId, tenantId, limit] as const

export function usePartnerLedgerQuery(limit: number) {
  const { user, isAuthenticated } = useAuth()

  return useInfiniteQuery({
    queryKey: partnerLedgerQueryKey(user?.user_id ?? "unauthenticated", getQueryTenantId(user), limit),
    enabled: isAuthenticated && Boolean(user?.user_id),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PartnerLedgerPage> => {
      const result = await listPartnerLedgerAction({ limit, offset: pageParam })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    getNextPageParam: lastPage => lastPage.next_offset ?? undefined,
  })
}

export const partnerPayoutsQueryPrefix = ["partner-payouts"] as const

export const partnerPayoutsQueryKey = (userId: string, tenantId: string) =>
  [...partnerPayoutsQueryPrefix, userId, tenantId] as const

export function usePartnerPayoutsQuery() {
  const { user, isAuthenticated } = useAuth()

  return useQuery<PartnerPayoutView[]>({
    queryKey: partnerPayoutsQueryKey(user?.user_id ?? "unauthenticated", getQueryTenantId(user)),
    enabled: isAuthenticated && Boolean(user?.user_id),
    queryFn: async () => {
      const result = await listPartnerPayoutsAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const adminPayoutRequestsQueryPrefix = ["admin-payout-requests"] as const

export const adminPayoutRequestsQueryKey = (userId: string, status: PartnerPayoutStatus) =>
  [...adminPayoutRequestsQueryPrefix, userId, status] as const

export function useAdminPayoutRequestsQuery(status: PartnerPayoutStatus) {
  const { user, isAuthenticated } = useAuth()

  return useQuery<AdminPayoutRequestView[]>({
    queryKey: adminPayoutRequestsQueryKey(user?.user_id ?? "unauthenticated", status),
    enabled: isAuthenticated && Boolean(user?.user_id),
    queryFn: async () => {
      const result = await listAdminPayoutRequestsAction({ status })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const adminPartnersQueryPrefix = ["admin-partners"] as const

export const adminPartnersQueryKey = (userId: string) => [...adminPartnersQueryPrefix, userId] as const

export function useAdminPartnersQuery() {
  const { user, isAuthenticated } = useAuth()

  return useQuery<AdminPartnerView[]>({
    queryKey: adminPartnersQueryKey(user?.user_id ?? "unauthenticated"),
    enabled: isAuthenticated && Boolean(user?.user_id),
    queryFn: async () => {
      const result = await listAdminPartnersAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const adminPartnerDetailQueryPrefix = ["admin-partner-detail"] as const

export const adminPartnerDetailQueryKey = (userId: string, partnerId: string) =>
  [...adminPartnerDetailQueryPrefix, userId, partnerId] as const

export function useAdminPartnerDetailQuery(partnerId: string | null) {
  const { user, isAuthenticated } = useAuth()

  return useQuery<AdminPartnerDetail>({
    queryKey: adminPartnerDetailQueryKey(user?.user_id ?? "unauthenticated", partnerId ?? "none"),
    enabled: isAuthenticated && Boolean(user?.user_id) && Boolean(partnerId),
    queryFn: async () => {
      const result = await getAdminPartnerDetailAction({ partner_id: partnerId as string })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const adminProgramSettingsQueryPrefix = ["admin-partner-program-settings"] as const

export const adminProgramSettingsQueryKey = (userId: string) =>
  [...adminProgramSettingsQueryPrefix, userId] as const

export function useAdminProgramSettingsQuery() {
  const { user, isAuthenticated } = useAuth()

  return useQuery<AdminProgramSettings>({
    queryKey: adminProgramSettingsQueryKey(user?.user_id ?? "unauthenticated"),
    enabled: isAuthenticated && Boolean(user?.user_id),
    queryFn: async () => {
      const result = await getAdminProgramSettingsAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}
